"use client";

/**
 * GuestTracker — silent background component.
 * Fires once per route change for all users, incrementing separate
 * aggregate counters for guests vs logged-in users in Firestore.
 * No PII is ever collected or stored — user IDs are only used for
 * per-session deduplication and never written to Firestore.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { trackGuestPageView, trackUserPageView } from "@/services/guest-analytics";

export function GuestTracker() {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const lastGuestTracked = useRef<string | null>(null);
  const lastUserTracked = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      // ── Unauthenticated visitor ─────────────────────────────────────────
      if (lastGuestTracked.current === pathname) return;
      lastGuestTracked.current = pathname;
      trackGuestPageView(pathname);
    } else {
      // ── Logged-in user (separate counter block, no PII stored) ─────────
      const key = `${user.uid}::${pathname}`;
      if (lastUserTracked.current === key) return;
      lastUserTracked.current = key;
      trackUserPageView(pathname, user.uid);
    }
  }, [pathname, user, loading]);

  return null;
}
